import React, { useState } from 'react';
import { format, isToday, isThisWeek } from 'date-fns';
import { Task, TaskPriority, ExternalTask } from '../types';
import { useStandUp } from '../contexts/StandUpContext';
import { desktop } from '../platform';
import {
    AlertCircle, CheckCircle2, Clock, Zap, Pencil, Trash2,
    Play, Pause, Timer, Plug, Loader2, ExternalLink, MessageSquare, RefreshCw, CloudUpload,
    Layers
} from 'lucide-react';
import AlertModal from './AlertModal';
import PromptModal from './PromptModal';

interface TaskCardProps {
    task: Task | ExternalTask;
    compact?: boolean;
    fixedHeight?: boolean;
    showAssignee?: boolean;
}

const PriorityBadge: React.FC<{ priority: TaskPriority }> = ({ priority }) => {
    const colors = {
        low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        medium: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
        high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return (
        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${colors[priority]}`}>
            {priority}
        </span>
    );
};

const formatDuration = (start: number, end: number) => {
    const diffMs = end - start;
    if (diffMs < 0) return '';
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
};

const TaskCard: React.FC<TaskCardProps> = ({ task, fixedHeight = false, showAssignee = false }) => {
    const { isTaskBlocked, getDependencies, updateTaskStatus, updateTask, allUsers, openModal, openDetailModal, deleteTask, userProfile, getTeamName, getTeamColor, showToast } = useStandUp();
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [showBlockPrompt, setShowBlockPrompt] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isLongSyncing, setIsLongSyncing] = useState(false);

    const blocked = isTaskBlocked(task);
    const dependencies = getDependencies(task);
    const blockedByDeps = dependencies.some(d => d.status !== 'done');
    const assignee = allUsers.find(u => u.id === task.assigneeId);
    
    const isExternal = 'externalSource' in task;
    const extTask = task as ExternalTask;
    const isSyncing = (task as any).syncing;

    // Only show the "Amber" syncing state if it takes longer than 2 seconds (indicates offline/slow)
    React.useEffect(() => {
        let timer: any;
        if (isSyncing) {
            timer = setTimeout(() => setIsLongSyncing(true), 2000);
        } else {
            setIsLongSyncing(false);
        }
        return () => clearTimeout(timer);
    }, [isSyncing]);

    const formatTimeSmart = (time: any) => {
        if (!time) return '...';
        try {
            const date = typeof time.toDate === 'function' ? time.toDate() : new Date(time);
            if (isNaN(date.getTime())) return '...';
            
            const diffInMinutes = Math.floor((Date.now() - date.getTime()) / 60000);

            // 1. Less than 1 hour ago
            if (diffInMinutes < 60) {
                return `${diffInMinutes}m ago`;
            }

            // 2. Today
            if (isToday(date)) {
                return format(date, 'h:mm a');
            }

            // 3. This Week
            if (isThisWeek(date)) {
                return format(date, 'E, h:mm a');
            }

            // 4. Older
            return format(date, 'MMM d');
        } catch (e) {
            return '...';
        }
    };

    const truncateText = (text: string, limit: number) => {
        if (!text || text.length <= limit) return text;
        return text.slice(0, limit) + '...';
    };

    // Show team badge if user is viewing multiple teams, but ONLY for native tasks
    // External tasks are project-owned and might belong to multiple teams.
    const showTeamBadge = !isExternal && (userProfile?.activeTeamIds?.length || 0) > 1;
    const teamName = showTeamBadge ? getTeamName((task as Task).teamId) : '';
    const teamColorObj = showTeamBadge ? getTeamColor((task as Task).teamId) : { style: '', dot: '' };

    // --- Workflow Handlers ---
    const handleAction = async (fn: () => Promise<void>) => {
        setIsUpdating(true);
        try {
            await fn();
            // Tiny delay to allow Firestore listener to catch the 'hasPendingWrites' state
            await new Promise(resolve => setTimeout(resolve, 150));
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePlay = () => handleAction(async () => await updateTaskStatus(task.id, 'in-progress'));
    const handlePause = () => handleAction(async () => await updateTaskStatus(task.id, 'todo'));
    const handleComplete = () => handleAction(async () => await updateTaskStatus(task.id, 'done'));
    const handleUndo = () => handleAction(async () => await updateTaskStatus(task.id, 'todo'));

    const toggleDeepWork = () => handleAction(async () => {
        if (task.status === 'deep-work') await updateTaskStatus(task.id, 'in-progress');
        else await updateTaskStatus(task.id, 'deep-work');
    });

    const toggleBlocked = () => {
        if (task.status === 'blocked') {
            handleAction(async () => await updateTask(task.id, { status: 'todo', blockedReason: '' }));
        } else {
            setShowBlockPrompt(true);
        }
    };

    const confirmBlock = (reason: string) => {
        handleAction(async () => await updateTask(task.id, { status: 'blocked', blockedReason: reason }));
        setShowBlockPrompt(false);
    };

    const handleDelete = () => {
        deleteTask(task.id);
        setShowDeleteAlert(false);
    };

    // --- Visual Logic ---

    const getStatusStyles = () => {
        if (isLongSyncing) return 'border-t-4 border-t-amber-400 dark:border-t-amber-400 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm opacity-90';
        if (task.status === 'blocked') return 'border-t-4 border-t-red-500 dark:border-t-red-500 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';
        
        // NEW: Dependency-blocked style
        if (task.status === 'todo' && blockedByDeps) return 'border-t-4 border-t-yellow-600 dark:border-t-yellow-600 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';

        if (task.status === 'deep-work') return 'border-t-4 border-t-purple-500 dark:border-t-purple-500 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';
        if (task.status === 'in-progress') return 'border-t-4 border-t-blue-500 dark:border-t-blue-500 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';
        if (task.status === 'done') return 'border-t-4 border-t-emerald-500/50 dark:border-t-emerald-500/50 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:border-t-emerald-500';
        return 'border-t-4 border-t-gray-100 dark:border-t-gray-700/50 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-t-brand-400 dark:hover:border-t-brand-500 shadow-sm';
    };

    return (
        <>
            <div className={`group relative p-3.5 rounded-xl border transition-[border-color,box-shadow,transform] transform-gpu duration-200 hover:z-10 ${getStatusStyles()} flex flex-col gap-2.5 ${fixedHeight ? 'h-[180px]' : 'h-auto min-h-[110px]'} w-full text-left`}>
                {/* Top Row: Hyper-compact Meta */}
                <div className="flex items-center justify-between gap-2 shrink-0">
                    <div className="flex items-center gap-2 overflow-hidden">
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter border shrink-0 ${
                            isLongSyncing ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-800/50 animate-pulse' :
                            task.status === 'blocked' ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-100 dark:border-red-800/50' :
                            task.status === 'deep-work' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-800/50' :
                            task.status === 'in-progress' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800/50' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600'
                        }`}>
                            {isLongSyncing ? 'Syncing' : task.status.replace('-', ' ')}
                        </span>
                        
                        {(task.taskKey || (isExternal && extTask.externalId)) && (
                            <span 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const id = task.taskKey || extTask.externalId || '';
                                    const textToCopy = `${id}: ${task.title}`;
                                    navigator.clipboard.writeText(textToCopy);
                                    showToast(`${id} copied!`);
                                }}
                                className="text-[10px] font-bold font-mono text-gray-400 dark:text-gray-500 truncate uppercase tracking-tighter cursor-pointer hover:text-brand-500 transition-colors"
                                title="Click to copy ID and Title"
                            >
                                {task.taskKey || `${extTask.externalSource === 'openproject' ? '#' : ''}${extTask.externalId}`}
                            </span>
                        )}

                        <div className="flex items-center gap-1 ml-1 shrink-0">
                            <div className={`w-1.5 h-1.5 rounded-full ${
                                task.priority === 'high' || task.priority === 'critical' ? 'bg-orange-500' : 
                                task.priority === 'medium' ? 'bg-blue-400' : 'bg-gray-300'
                            }`} />
                            <span className="text-[9px] font-black uppercase text-gray-400">{task.priority}</span>
                        </div>

                        {task.status === 'done' && task.startedAt && task.completedAt && (
                            <div className="flex items-center gap-1 ml-1 shrink-0 text-emerald-600 dark:text-emerald-400" title="Time taken">
                                <Timer size={10} />
                                <span className="text-[9px] font-black uppercase tracking-tighter">
                                    {formatDuration(task.startedAt, task.completedAt)}
                                </span>
                            </div>
                        )}
                    </div>

                    {isLongSyncing && <CloudUpload size={14} className="text-amber-500 animate-bounce" />}
                </div>

                {/* Content Area: Compressed vertical space */}
                <div className="flex-1 min-h-0 overflow-hidden space-y-2">
                    <div className="flex items-start gap-2 group/title">
                        <h3 
                            onClick={() => openDetailModal(task)}
                            className={`text-[13px] font-bold text-gray-900 dark:text-white leading-tight line-clamp-2 flex-1 cursor-pointer hover:text-brand-600 dark:hover:text-brand-400 transition-colors ${task.status === 'done' ? 'line-through opacity-80' : ''}`}
                        >
                            {task.title}
                        </h3>
                        {isExternal && extTask.externalUrl && (
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    desktop.openUrl(extTask.externalUrl!);
                                }}
                                className="p-1 text-gray-400 hover:text-brand-500 transition-all opacity-0 group-hover/title:opacity-100" 
                                title={`Open in ${extTask.externalSource}`}
                            >
                                <ExternalLink size={10} />
                            </button>
                        )}
                    </div>
                    
                    {/* Dependencies: Inline Flow */}
                    {(dependencies.length > 0 || (fixedHeight && task.comments && task.comments.length > 0 && !task.blockedReason)) && (
                        <div className="flex flex-nowrap gap-1 items-center overflow-hidden">
                            {dependencies.slice(0, 2).map(dep => (
                                <div key={dep.id} className="flex items-center gap-0.5 bg-gray-50 dark:bg-gray-800/50 px-1 py-0.5 rounded border border-gray-100 dark:border-gray-700 shrink-0 max-w-[90px]">
                                    {dep.status === 'done' ? <CheckCircle2 size={7} className="text-emerald-500" /> : <Clock size={7} className="text-amber-500" />}
                                    <span className={`${dep.status === 'done' ? 'line-through opacity-50' : 'font-bold'} text-[8px] text-gray-500 truncate`}>{dep.title}</span>
                                </div>
                            ))}

                            {dependencies.length > 2 && (
                                <div className="text-[8px] font-black text-gray-400 bg-gray-50 dark:bg-gray-800/50 px-1 py-0.5 rounded border border-gray-100 dark:border-gray-700 shrink-0">
                                    +{dependencies.length - 2}
                                </div>
                            )}
                            
                            {/* Condensed Comment Indicator (Only if fixedHeight + has deps) */}
                            {fixedHeight && dependencies.length > 0 && task.comments && task.comments.length > 0 && !task.blockedReason && (
                                <div 
                                    onClick={(e) => { e.stopPropagation(); openDetailModal(task); }}
                                    className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800/50 px-1 py-0.5 rounded border border-gray-100 dark:border-gray-700 shrink-0 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <MessageSquare size={10} className="text-gray-400" />
                                    <span className="text-[8px] font-black text-gray-400">{task.comments.length}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Context: Refined, no-box approach */}
                    {task.status === 'blocked' && task.blockedReason ? (
                        <div 
                            onClick={() => openDetailModal(task)}
                            className="pl-2 border-l-2 border-red-500/50 py-0.5 cursor-pointer hover:border-red-500 transition-all flex items-center gap-2"
                        >
                            <p className="text-[10px] text-red-700 dark:text-red-400 italic leading-tight line-clamp-2 flex-1">
                                <span className="not-italic font-black text-[8px] uppercase mr-1">Blocked:</span>
                                {task.blockedReason}
                            </p>
                            {task.comments && task.comments.length > 0 && (
                                <div className="shrink-0 flex items-center gap-1 bg-gray-50 dark:bg-gray-800 px-1 rounded-md text-[9px] font-black text-gray-400 dark:text-gray-500">
                                    <MessageSquare size={10} />
                                    <span>{task.comments.length}</span>
                                </div>
                            )}
                        </div>
                    ) : task.comments && task.comments.length > 0 && !(fixedHeight && dependencies.length > 0) ? (
                        <div 
                            onClick={() => openDetailModal(task)}
                            className="pl-2 border-l-2 border-brand-500/30 py-0.5 cursor-pointer hover:border-brand-500 transition-all flex items-center gap-2"
                        >
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 italic leading-tight line-clamp-2 flex-1">
                                <span className="font-bold not-italic text-gray-700 dark:text-gray-200 mr-1">{task.comments[task.comments.length - 1].userName.split(' ')[0]}:</span>
                                {task.comments[task.comments.length - 1].text}
                            </p>
                            {task.comments.length > 1 && (
                                <span className="shrink-0 text-[9px] font-black text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 px-1 rounded-md">
                                    +{task.comments.length - 1}
                                </span>
                            )}
                        </div>
                    ) : null}
                </div>

                {/* Bottom Bar: Tight Metadata & Multi-action Bar */}
                <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 dark:border-gray-700 mt-auto shrink-0">
                    <div className="flex items-center gap-1 text-gray-400">
                        <RefreshCw size={9} className={isSyncing || isUpdating ? "animate-spin" : ""} />
                        <span className="text-[9px] font-bold">{formatTimeSmart(task.updatedAt)}</span>
                    </div>

                    <div className="flex items-center gap-0">
                        {isUpdating ? (
                            <div className="px-3 flex items-center justify-center text-brand-500">
                                <Loader2 size={14} className="animate-spin" />
                            </div>
                        ) : (
                            <>
                                {/* Workflow Actions */}
                                {task.status === 'done' ? (
                                    <button onClick={handleUndo} className="p-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-all" title="Undo"><RefreshCw size={13} /></button>
                                ) : (
                                    <>
                                        {blockedByDeps && task.status === 'todo' ? (
                                            <div className="p-1.5 text-yellow-600 dark:text-yellow-500 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-100 dark:border-yellow-800 rounded-lg" title="Waiting for dependencies">
                                                <Clock size={13} />
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={task.status === 'todo' ? handlePlay : handlePause}
                                                className={`p-1.5 rounded-lg transition-all ${task.status === 'deep-work' || task.status === 'in-progress' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30'}`} 
                                                title={task.status === 'todo' ? 'Start' : 'Pause'}
                                            >
                                                {task.status === 'deep-work' || task.status === 'in-progress' ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
                                            </button>
                                        )}

                                        {/* Only show Done if task is active */}

                                {(task.status === 'in-progress' || task.status === 'deep-work') && (
                                    <button 
                                        onClick={handleComplete} 
                                        className="p-1.5 ml-0.5 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-800 rounded-lg transition-all hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                                        title="Done"
                                    >
                                        <CheckCircle2 size={13} />
                                    </button>
                                )}
                            </>
                        )}
                            </>
                        )}

                        <div className="w-px h-3 bg-gray-100 dark:bg-gray-700 mx-0.5" />

                        {/* Secondary Actions */}
                        {!isExternal && (
                            <button onClick={() => openModal(task)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all" title="Edit"><Pencil size={13} /></button>
                        )}
                        <button 
                            onClick={toggleDeepWork}
                            className={`p-1.5 rounded-lg transition-all ${task.status === 'deep-work' ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'}`} 
                            title="Deep Work"
                        >
                            <Zap size={13} fill={task.status === 'deep-work' ? "currentColor" : "none"} />
                        </button>
                        <button 
                            onClick={toggleBlocked}
                            className={`p-1.5 rounded-lg transition-all ${task.status === 'blocked' ? 'text-red-600 bg-red-50 dark:bg-red-900/30' : 'text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30'}`} 
                            title={task.status === 'blocked' ? 'Unblock' : 'Block'}
                        >
                            <AlertCircle size={13} />
                        </button>
                        <button onClick={() => openDetailModal(task)} className="p-1.5 text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/30 rounded-lg transition-all" title="Discussion">
                            <MessageSquare size={13} />
                        </button>
                        <button onClick={() => setShowDeleteAlert(true)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all" title="Delete">
                            <Trash2 size={13} />
                        </button>
                    </div>
                </div>

                {/* Labels at top */}
                <div className="absolute top-[-8px] right-3 flex items-center gap-1">
                    {(teamName || (isExternal && extTask.metadata?.projectName)) && (
                        <div className={`px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter shadow-sm border dark:bg-opacity-100 flex items-center gap-1 ${isExternal ? 'dark:bg-gray-800 bg-white text-gray-400 border-gray-200 dark:border-gray-600' : teamColorObj.style}`}>
                            {isExternal && <Layers size={8} className="text-gray-400" />}
                            <span>{isExternal ? extTask.metadata?.projectName : teamName}</span>
                        </div>
                    )}
                    {isExternal && extTask.externalSource && (
                        <div className="bg-white dark:bg-gray-800 dark:bg-opacity-100 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-[8px] font-black text-gray-400 uppercase tracking-tighter shadow-sm flex items-center gap-1">
                            <Plug size={8} className="text-indigo-500 dark:text-indigo-400" />
                            <span>{extTask.externalSource}</span>
                        </div>
                    )}
                </div>


            </div>

            <AlertModal
                isOpen={showDeleteAlert}
                title="Delete Task"
                message="Are you sure you want to delete this task? This cannot be undone."
                confirmText="Delete"
                isDestructive={true}
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteAlert(false)}
            />

            <PromptModal
                isOpen={showBlockPrompt}
                title="What is blocking this task?"
                placeholder="e.g. Waiting on API deployment..."
                confirmText="Block Task"
                isDestructive={true}
                onConfirm={confirmBlock}
                onCancel={() => setShowBlockPrompt(false)}
            />
        </>
    );
};

export default TaskCard;
